import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Compass, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";

// SC-1 dashboard tile. Sits on the Project page next to the KPI grid as a
// SHORTCUT entry point into Survey Control (the official home is
// Project → Settings → Survey Control via the Setup ribbon).
//
// Truthful by design: RED/AMBER/GREEN reflects the same status endpoint
// that powers the page header. Operator should be able to glance and
// know if field positioning is ready in under 5 seconds.

const STYLES = {
  red: {
    bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.45)",
    text: "rgb(239,68,68)", label: "NOT READY",
  },
  amber: {
    bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.45)",
    text: "rgb(245,158,11)", label: "IN PROGRESS",
  },
  green: {
    bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.45)",
    text: "rgb(34,197,94)", label: "READY",
  },
};

export default function SurveyControlTile({ projectId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/survey-control/status`);
        if (!cancelled) { setStatus(r.data); setLoading(false); }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) {
    return (
      <div className="fop-surface-2 rounded-lg p-4" data-testid="survey-control-tile-loading">
        <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Loading Survey Control…</div>
      </div>
    );
  }
  if (!status) return null;

  const s = STYLES[status.overall_state] || STYLES.red;
  const isReady = status.field_positioning_ready;

  return (
    <Link
      to={`/project/${projectId}/survey-control`}
      className="block rounded-lg p-4 border-2 transition-colors hover:border-[var(--fop-primary)]"
      style={{ background: s.bg, borderColor: s.border }}
      data-testid="survey-control-tile"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-full p-2 shrink-0" style={{ background: s.bg }}>
          <Compass size={20} style={{ color: s.text }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Survey Control</div>
          <div className="font-display text-base text-[var(--fop-text)] mt-0.5 leading-tight">
            Field Positioning:&nbsp;
            <span style={{ color: s.text }} data-testid="survey-tile-state-label">{s.label}</span>
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1 text-[11px] font-mono">
            <Stat label="COORD SYS" ok={status.coordinate_system_configured} />
            <Stat label="POINTS" count={status.control_points_count} />
            <Stat label="BENCHMARKS" count={status.benchmarks_count} />
            <Stat label="CALIBRATED"
                    ok={status.sheets_total === 0 ? null : status.sheets_calibrated > 0}
                    count={`${status.sheets_calibrated}/${status.sheets_total}`} />
          </div>
          {!isReady && status.missing && status.missing.length > 0 && (
            <div className="text-[10px] font-mono uppercase tracking-wider mt-2" style={{ color: s.text }} data-testid="survey-tile-missing">
              <AlertTriangle size={10} className="inline mr-1" />
              {status.missing[0]}
              {status.missing.length > 1 && <span className="text-[var(--fop-text-3)]"> · +{status.missing.length - 1} more</span>}
            </div>
          )}
          {isReady && (
            <div className="text-[10px] font-mono uppercase tracking-wider mt-2 inline-flex items-center gap-1" style={{ color: s.text }}>
              <CheckCircle2 size={10} /> Ready for field use
            </div>
          )}
        </div>
        <ChevronRight size={14} className="text-[var(--fop-text-3)] shrink-0 mt-1" />
      </div>
    </Link>
  );
}

function Stat({ label, ok, count }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">{label}</div>
      <div className="text-[var(--fop-text)] flex items-center gap-1">
        {ok === true && <CheckCircle2 size={10} className="text-[rgb(34,197,94)]" />}
        {ok === false && <AlertTriangle size={10} className="text-[rgb(239,68,68)]" />}
        <span>{count !== undefined ? count : (ok ? "OK" : "—")}</span>
      </div>
    </div>
  );
}
